/**
 * Copyright (C) 2020 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useRef, useMemo, useContext, useLayoutEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled, { withTheme } from 'styled-components';
import GoogleMapsControl, { MapControlOrderIndex } from '../map/GoogleMapsControl';
import StyledProps from '../theme/AppThemeInterface';
import Label from '@mui/icons-material/Label';
import { MapActionsContext, useSelectorWithMapId } from '../map/BaseMapContainer';
import { selectTailTagOverlaysPanelShown } from '../../../store';
import { Tooltip } from '@mui/material';
interface TailTagControlProps extends StyledProps {
  map: google.maps.Map;
  isFullScreen: boolean;
}

export function TailTagControl({ theme, map, isFullScreen }: TailTagControlProps) {
  const dispatch = useDispatch();
  const mapActionsContext = useContext(MapActionsContext);
  const tailTagOverlaysPanelShown = useSelectorWithMapId(selectTailTagOverlaysPanelShown);
  const { toggleTailTagOverlaysPanel } = mapActionsContext.mapActions;

  const tailTagControlIcon = useRef<HTMLElement>();

  useLayoutEffect(() => {
    if (tailTagControlIcon.current && tailTagControlIcon.current.classList) {
      if (tailTagOverlaysPanelShown) {
        tailTagControlIcon.current.classList.add('tail_tag_overlays_panel_icon--active');
      } else {
        tailTagControlIcon.current.classList.remove('tail_tag_overlays_panel_icon--active');
      }
    }
  }, [tailTagOverlaysPanelShown]);

  const items = useMemo(
    () => [
      {
        render: () => (
          <Tooltip
            arrow
            title="Tag configuration"
            placement="left-start"
            componentsProps={{
              tooltip: {
                sx: { backgroundColor: theme.tooltipBackgroundColor },
              },
              arrow: {
                sx: { color: theme.tooltipBackgroundColor },
              },
            }}
          >
            <StyledLabel id="tail_tag_overlays_panel_btn" ref={tailTagControlIcon} />
          </Tooltip>
        ),
        buttonTextColor: theme.secondaryButtonTextColor,
        id: 'tail_tag_overlays_panel_btn_container',
        onClick: () => {
          dispatch(toggleTailTagOverlaysPanel());
        },
      },
    ],
    [tailTagControlIcon, dispatch, theme, toggleTailTagOverlaysPanel],
  );

  return (
    <GoogleMapsControl
      items={items}
      map={map}
      mapPosition={google.maps.ControlPosition.RIGHT_TOP}
      height={32}
      hidden={isFullScreen}
      index={MapControlOrderIndex.TAIL_TAGS_CONTOL}
    />
  );
}

export default withTheme(TailTagControl);

const StyledLabel = styled(Label)`
  width: 20px !important;
`;
